import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faNetworkWired,
  faRobot,
  faBrain,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

const AddConnectorModal = ({ isOpen, onClose, onSelectConnectorType }) => {
  const [selectedType, setSelectedType] = useState(null);

  const connectorTypes = [
    {
      id: 'netsuite',
      name: 'NetSuite',
      description: 'Connect to NetSuite ERP system with OAuth1 authentication',
      icon: faNetworkWired,
      iconBg: 'from-blue-500 to-blue-600',
      category: 'ERP System',
      features: ['Customer Data', 'Item Management', 'RFQ Processing', 'Workflow Integration']
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      description: 'Connect to Salesforce CRM for sales order processing',
      icon: faNetworkWired,
      iconBg: 'from-cyan-500 to-blue-600',
      category: 'CRM System',
      features: ['Account Management', 'Opportunity Creation', 'Product Catalog', 'Sales Orders']
    },
    {
      id: 'openai',
      name: 'OpenAI',
      description: 'Connect to OpenAI GPT models for AI-powered automation',
      icon: faRobot,
      iconBg: 'from-green-500 to-emerald-600',
      category: 'AI Provider',
      features: ['GPT Models', 'Text Generation', 'Chat Completion', 'AI Analysis']
    },
    {
      id: 'anthropic',
      name: 'Anthropic Claude',
      description: 'Connect to Anthropic Claude for advanced AI capabilities',
      icon: faBrain,
      iconBg: 'from-purple-500 to-indigo-600',
      category: 'AI Provider',
      features: ['Claude Models', 'Reasoning', 'Analysis', 'Document Processing']
    },
    {
      id: 'microsoft',
      name: 'Microsoft 365',
      description: 'Connect to Microsoft 365 services via Azure AD',
      icon: faNetworkWired,
      iconBg: 'from-blue-500 to-cyan-500',
      category: 'Cloud Platform',
      features: ['Outlook Email', 'OneDrive', 'SharePoint', 'Teams Integration']
    }
  ];

  const handleContinue = () => {
    if (selectedType) {
      onSelectConnectorType(selectedType);
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                  Add New Connector
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Choose a connector type to integrate with your platform
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg sm:text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 overflow-y-auto max-h-[60vh] sm:max-h-[65vh]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {connectorTypes.map((connector) => (
                <motion.div
                  key={connector.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`relative cursor-pointer border-2 rounded-lg p-3 sm:p-4 transition-all duration-200 ${
                    selectedType === connector.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => setSelectedType(connector.id)}
                >
                  <div className="text-center space-y-2 sm:space-y-3">
                    {/* Icon */}
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-gradient-to-r ${connector.iconBg} rounded-lg flex items-center justify-center text-white`}>
                      <FontAwesomeIcon icon={connector.icon} className="text-xl sm:text-2xl" />
                    </div>

                    {/* Title & Category */}
                    <div>
                      <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">
                        {connector.name}
                      </h3>
                      <span className="inline-block px-2 py-0.5 sm:py-1 bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-400 rounded-full mt-1">
                        {connector.category}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {connector.description}
                    </p>

                    {/* Features - Hidden on mobile, show on larger screens */}
                    <div className="hidden sm:block space-y-2">
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Key Features
                      </h4>
                      <div className="space-y-1">
                        {connector.features.map((feature, index) => (
                          <div key={index} className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    {selectedType === connector.id && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-2 right-2 w-5 h-5 sm:w-6 sm:h-6 bg-indigo-500 rounded-full flex items-center justify-center"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-white text-xs rotate-45" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Selected Connector Info */}
            {selectedType && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 sm:mt-6 p-3 sm:p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm sm:text-base text-indigo-900 dark:text-indigo-200">
                      {connectorTypes.find(c => c.id === selectedType)?.name} Selected
                    </h4>
                    <p className="text-xs sm:text-sm text-indigo-700 dark:text-indigo-300 mt-1">
                      You'll be taken to the configuration form next
                    </p>
                  </div>
                  <div className="text-indigo-500">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col-reverse sm:flex-row justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
              <button
                onClick={handleClose}
                className="btn btn-secondary w-full sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={handleContinue}
                disabled={!selectedType}
                className={`btn w-full sm:w-auto ${selectedType ? 'btn-primary' : 'btn-disabled'}`}
              >
                <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
                Continue
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AddConnectorModal;